import { useState } from 'react';
import axios from 'axios';
import {
  Card, Input, Select, Modal, Form, Typography, Checkbox,
} from 'antd';
import { API_URL } from '../../config';
import type { Composicao, Proposta } from './types';
import { getToken } from './utils';

const { Text } = Typography;

// Modal "Nova composição": cria do zero ou clona template.
export default function FormNova({ propostas, templates, markupBase, propostaInicialId, onClose, onCriada }: {
  propostas: Proposta[]; templates: Composicao[];
  markupBase: any | null;
  propostaInicialId?: number;
  onClose: () => void; onCriada: (nova: any) => void;
}) {
  const propInicial = propostaInicialId ? propostas.find(p => String(p.id) === String(propostaInicialId)) : null;
  // Markup padrão automático = acréscimo % calculado a partir do /markup-base (ADM 12m + impostos + lucro 20%).
  // Fallback 30% se backend não respondeu.
  const markupAutoPct = markupBase?.acrescimo_pct != null && markupBase.acrescimo_pct > 0
    ? markupBase.acrescimo_pct.toFixed(2)
    : '30';
  const [form, setForm] = useState({
    nome: propInicial ? `Composição — ${propInicial.titulo}` : '',
    descricao: '',
    proposta_id: propostaInicialId ? String(propostaInicialId) : '',
    markup_padrao_pct: markupAutoPct,
    is_template: false, template_id: '',
  });
  const [salvando, setSalvando] = useState(false);

  async function criar() {
    if (!form.nome) { alert('Nome obrigatório'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      let res;
      if (form.template_id) {
        // Cria a partir de template (clona itens)
        res = await axios.post(`${API_URL}/comercial/composicoes/${form.template_id}/clonar`, {
          nome: form.nome,
          proposta_id: form.proposta_id || null,
          is_template: form.is_template,
        }, { headers: { Authorization: token } });
      } else {
        // Cria do zero
        res = await axios.post(`${API_URL}/comercial/composicoes`, {
          nome: form.nome,
          descricao: form.descricao,
          proposta_id: form.proposta_id || null,
          markup_padrao_pct: parseFloat(form.markup_padrao_pct) || 30,
          is_template: form.is_template,
        }, { headers: { Authorization: token } });
      }
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      if (!data || !data.id) {
        alert('Erro: resposta inválida do servidor.\n' + JSON.stringify(data).slice(0, 300));
        return;
      }
      onCriada(data);
    } catch (e: any) {
      alert('Erro ao criar composição: ' + (e.response?.data?.erro || e.response?.data?.error || e.response?.data?.message || e.message));
    }
    finally { setSalvando(false); }
  }

  return (
    <Modal open onCancel={onClose} onOk={criar} confirmLoading={salvando} width={580} destroyOnClose
      title="Nova composição de preços"
      okText={salvando ? 'Criando…' : (form.template_id ? 'Criar do template' : 'Criar e editar')}
      cancelText="Cancelar">
      {templates.length > 0 && (
        <Card size="small" style={{ background: '#FFF8E8', borderColor: '#B07500', marginBottom: 14 }} bodyStyle={{ padding: 10 }}>
          <Form.Item label={<Text strong style={{ color: '#B07500' }}>📋 Partir de um template (opcional)</Text>}
            extra="Templates copiam todos os itens. Ajuste quantidades e preços depois." style={{ marginBottom: 0 }}>
            <Select value={form.template_id || undefined} onChange={(v) => setForm({ ...form, template_id: v || '' })}
              placeholder="Começar do zero" allowClear
              options={templates.map(t => ({ label: `${t.nome} (${t.n_itens} itens)`, value: t.id }))} />
          </Form.Item>
        </Card>
      )}

      <Form layout="vertical" size="small">
        <Form.Item label="Nome" required>
          <Input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })}
            placeholder="Ex.: Batimetria Taquari-Antas - SEMA" />
        </Form.Item>
        {!form.template_id && (
          <Form.Item label="Descrição">
            <Input.TextArea value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} rows={2} />
          </Form.Item>
        )}
        <Form.Item label="Vincular à proposta">
          <Select value={form.proposta_id || undefined} onChange={(v) => setForm({ ...form, proposta_id: v || '' })}
            placeholder="Sem vínculo (composição genérica)" allowClear showSearch optionFilterProp="label"
            options={propostas.map(p => ({ label: p.titulo, value: p.id }))} />
        </Form.Item>
        {!form.template_id && (
          <Form.Item label="Acréscimo padrão (%)"
            extra={markupBase?.acrescimo_pct != null && markupBase.acrescimo_pct > 0
              ? `Pré-preenchido com o markup da empresa (${markupBase.markup_fator.toFixed(3)}× = +${markupBase.acrescimo_pct.toFixed(2)}%).`
              : 'Aplicado por padrão em novos itens. Cada item pode ter acréscimo próprio.'}>
            <Input type="number" step="0.01" value={form.markup_padrao_pct}
              onChange={e => setForm({ ...form, markup_padrao_pct: e.target.value })} />
          </Form.Item>
        )}
        <Checkbox checked={form.is_template} onChange={e => setForm({ ...form, is_template: e.target.checked })}>
          Salvar como template (modelo reutilizável)
        </Checkbox>
      </Form>
    </Modal>
  );
}
